const PropTypes = require('prop-types');
const React = require('react');
const reactUpdate = require('react-addons-update');
const Sortable = require('react-sortable-hoc');
const classNames = require('classnames');
const { Modal } = require('@reverbdotcom/discovery-ui');

const Photo = require('./photo.js');
const Editor = require('./editor.jsx');
const ThumbList = require('./thumb_list.jsx');
const Uploader = require('./uploader.jsx');
const CheckPhotoContents = require('./check_photo_contents.js');

const minimumDimension = 200; // (px) We will prevent images less than this on either isde.
const minimumWarnDimension = 620; // (px) We will warn them if less than this on either side.
const categorySelector = 'select[name="product[category_tree_ids][]"]';
const conditionSelector = 'select[name="product[condition]"]';

class Photos extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      photos: props.photos.map(photo => new Photo(photo)),
      editingPhoto: null,
      draggingIndex: null,
      isDroppingFile: false,
    };
    this.handleCloseEditor = this.handleCloseEditor.bind(this);
    this.handleUpdatePhoto = this.handleUpdatePhoto.bind(this);
    this.handlePhotoAdded = this.handlePhotoAdded.bind(this);
    this.handleFileDropStart = this.handleFileDropStart.bind(this);
    this.handleFileDropEnd = this.handleFileDropEnd.bind(this);
    this.handleEditPhoto = this.handleEditPhoto.bind(this);
    this.handleRemovePhoto = this.handleRemovePhoto.bind(this);
    this.handleSortEnd = this.handleSortEnd.bind(this);
    this.shouldCancelDrag = this.shouldCancelDrag.bind(this);
  }

  componentDidMount() {
    this.checkPrimaryImageContents();
    $(categorySelector).on('change', () => this.checkPrimaryImageContents());
    $(conditionSelector).on('change', () => this.checkPrimaryImageContents());
  }

  get photoContentsChecker() {
    if (!this.photoChecker) {
      this.photoChecker = new CheckPhotoContents(
        this.props.categories,
        this.props.imageDetectionPath,
      );
    }
    return this.photoChecker;
  }

  get isEditingPhoto() {
    return !!this.state.editingPhoto;
  }

  checkPrimaryImageContents(p) {
    if (!this.props.shouldDetectImages) { return null; }

    const photo = p || this.state.photos[0];
    const categoryId = this.props.categoryId || $(categorySelector).val();
    const condition = $(conditionSelector).val();
    return this.photoContentsChecker.checkContents({ categoryId, condition, photo })
      .then(resps => {
        if (resps.some(resp => resp.showWarning)) {
          const warnings = resps
            .filter(resp => resp.showWarning)
            .map(resp => resp.warningTypeName)
          this.setState({
            showPrimaryPhotoWarning: true,
            warningTypeNames: warnings,
            warnedPrimaryImageId: this.state.photos[0].public_id,
          });
        } else {
          this.setState({
            showPrimaryPhotoWarning: false,
          });
        }
      });
  }

  handleEditPhoto(photo) {
    this.setState({ editingPhoto: photo });
  }

  handleCloseEditor() {
    this.setState({ editingPhoto: null });
  }

  handleUpdatePhoto(updatedPhoto) {
    this.updatePhoto(updatedPhoto);
    this.setState({ editingPhoto: null });
  }

  handleRemovePhoto(photo) {
    this.removePhoto(photo);

    if (photo.showTooSmallWarning) {
      this.closeErrorMessage('showTooSmallWarning');
    }
  }

  removePhoto(photo) {
    const photoIndex = this.state.photos.indexOf(photo);
    const newPhotos = reactUpdate(this.state.photos, {
      $splice: [[photoIndex, 1]],
    });

    this.updatePhotos(newPhotos);
  }

  handlePhotoAdded(photo) {
    if (!this.props.requireMinimumDimensions || photo.meetsMinimumDimension(minimumWarnDimension)) {
      this.addPhoto(photo);
    } else if (photo.meetsMinimumDimension(minimumDimension)) {
      const newPhoto = photo.copy();
      newPhoto.showTooSmallWarning = true;
      this.addPhoto(newPhoto);
      this.setState({ showTooSmallWarning: true });
    } else {
      this.setState({ showTooSmallError: true });
    }
  }

  updatePhoto(updatedPhoto) {
    const photoIndex = this.state.photos.findIndex((photo) =>
      photo.public_id === updatedPhoto.public_id
    );

    const photos = this.state.photos;
    photos[photoIndex] = updatedPhoto;

    this.updatePhotos(photos);
  }

  updatePhotos(newPhotos) {
    const newPrimaryId = newPhotos[0] && newPhotos[0].public_id;
    const oldPrimaryId = this.state.photos[0] && this.state.photos[0].public_id;
    if (newPrimaryId && newPrimaryId !== oldPrimaryId) {
      this.checkPrimaryImageContents(newPhotos[0]);
    }
    this.setState({ photos: newPhotos });
  }

  addPhoto(photo) {
    const newPhotos = reactUpdate(this.state.photos, {
      $push: [photo],
    });

    this.updatePhotos(newPhotos);
  }

  handleSortEnd({ oldIndex, newIndex }) {
    this.updatePhotos(Sortable.arrayMove(this.state.photos, oldIndex, newIndex));
  }

  shouldCancelDrag(e) {
    if (e.target.tagName.toLowerCase() === 'a') {
      return true;
    }
    return false;
  }

  handleFileDropStart() {
    this.setState({ isDroppingFile: true });
  }

  handleFileDropEnd() {
    this.setState({ isDroppingFile: false });
  }

  closeErrorMessages() {
    this.setState({
      showTooSmallWarning: false,
      showTooSmallError: false,
      showPrimaryPhotoWarning: false,
    });
  }

  closeErrorMessage(stateKey) {
    const newState = {};
    newState[stateKey] = false;
    this.setState(newState);
  }

  dropFilesDisabled() {
    return $.browser.mozilla;
  }

  tipText() {
    if (this.state.photos.length === 0) {
      if (this.dropFilesDisabled()) {
        return I18n.t('js.listingForm.photos.tip.upload');
      }
      return I18n.t('js.listingForm.photos.tip.uploadOrDrop');
    }
    return I18n.t('js.listingForm.photos.tip.reorder');
  }

  renderEditor() {
    if (!this.isEditingPhoto) { return false; }

    return (
      <Editor
        photo={this.state.editingPhoto}
        onCloseEditor={this.handleCloseEditor}
        onUpdatePhoto={this.handleUpdatePhoto}
      />
    );
  }

  renderEditModal() {
    return (
      <Modal
        isOpen={this.isEditingPhoto}
      >
        {this.renderEditor()}
      </Modal>
    );
  }

  renderUploader() {
    return (
      <Uploader
        uploadOptions={this.props.uploadOptions}
        onFileAdded={this.handlePhotoAdded}
        onDragStart={this.handleFileDropStart}
        onDragEnd={this.handleFileDropEnd}
        dropFilesDisabled={this.dropFilesDisabled()}
      />
    );
  }

  renderError(stateKey, heading, message, cssModifier, warningName) {
    const className = classNames(
      'img-uploader__message',
      `img-uploader__message--${cssModifier}`
    );

    return (
      <div className={className} key={warningName}>
        <a
          className="img-uploader__message__close"
          onClick={() => this.closeErrorMessage(stateKey)}
        />
        <strong>
          {heading}
        </strong>
        <div className="small pt-half">
          {message}
        </div>
      </div>
    );
  }

  renderTooSmallWarning() {
    if (this.state.showTooSmallWarning) {
      return this.renderError(
        'showTooSmallWarning',
        I18n.t('js.listingForm.photos.smallImageWarningHeading'),
        I18n.t('js.listingForm.photos.smallImageWarning'),
        'warn'
      );
    }
    return null;
  }

  renderTooSmallError() {
    if (this.state.showTooSmallError) {
      return this.renderError(
        'showTooSmallError',
        I18n.t('js.listingForm.photos.smallImageErrorHeading'),
        I18n.t('js.listingForm.photos.smallImageError'),
        'error'
      );
    }
    return null;
  }

  renderPrimaryPhotoWarning() {
    if (this.state.showPrimaryPhotoWarning) {
      return this.state.warningTypeNames.map(warningName =>
        this.renderError(
          'showPrimaryPhotoWarning',
          I18n.t(`js.listingForm.photos.${warningName}WarningHeading`),
          I18n.t(`js.listingForm.photos.${warningName}Warning`),
          'warn',
          warningName
        )
      )
    }
    return null;
  }

  renderThumbs() {
    return (
      <ThumbList
        photos={this.state.photos}
        onEditPhoto={this.handleEditPhoto}
        onRemovePhoto={this.handleRemovePhoto}
        axis={'xy'}
        onSortEnd={this.handleSortEnd}
        shouldCancelStart={this.shouldCancelDrag}
        allowEdit={this.props.allowEdit}
      >
        {this.renderUploader()}
      </ThumbList>
    );
  }

  renderDropFileOverlay() {
    if (this.state.isDroppingFile) {
      return (
        <div className="img-uploader__drop-file-overlay">
          {I18n.t('js.listingForm.photos.drop_files')}
        </div>
      );
    }
    return null;
  }

  renderPhotosField() {
    const value = JSON.stringify(this.state.photos.map(photo => photo.attrsWithUpdates));
    return (
      <input
        type="hidden"
        name={`${this.props.inputName}[]`}
        value={value}
        className="img-uploader__photos-input"
      />
    );
  }

  renderPhotoOrderFields() {
    return this.state.photos.map((photo, index) => {
      const inputName = `photo_order[${photo.public_id}]`;
      return (
        <input
          type="hidden"
          name={inputName}
          value={index}
          key={photo.public_id}
          className="img-uploader__photo-order-input"
        />
      );
    });
  }

  renderImageDetectionFeedbackField() {
    if (!this.state.warnedPrimaryImageId) { return null; }
    const inputName = 'photo_detection_feedback';
    return (
      <input
        type="hidden"
        name={inputName}
        value={this.state.warnedPrimaryImageId}
      />
    );
  }

  renderTip() {
    if (!this.props.renderTip) { return null; }

    return (
      <div className="img-uploader__tip">
        {this.tipText()}
      </div>
    );
  }

  render() {
    return (
      <div ref="container" className="img-uploader">
        {this.renderTip()}
        {this.renderTooSmallError()}
        {this.renderTooSmallWarning()}
        {this.renderPrimaryPhotoWarning()}
        {this.renderThumbs()}
        {this.renderEditModal()}
        {this.renderDropFileOverlay()}
        {this.renderPhotosField()}
        {this.renderPhotoOrderFields()}
        {this.renderImageDetectionFeedbackField()}
      </div>
    );
  }
}

Photos.propTypes = {
  inputName: PropTypes.string.isRequired,
  uploadOptions: PropTypes.object.isRequired,
  photos: PropTypes.array,
  requireMinimumDimensions: PropTypes.bool,
  allowEdit: PropTypes.bool,
  renderTip: PropTypes.bool,
  categories: PropTypes.arrayOf(PropTypes.object).isRequired,
  imageDetectionPath: PropTypes.string.isRequired,
  shouldDetectImages: PropTypes.bool,
  categoryId: PropTypes.number,
};

Photos.defaultProps = {
  photos: [],
  requireMinimumDimensions: true,
  allowEdit: true,
  renderTip: true,
};

module.exports = Photos;



// WEBPACK FOOTER //
// ./javascripts/components/photos/photos.jsx